import type { UserMediaEntity } from '@/domain/entities/userMedia/userMediaEntity';

export const APP_EVENT_KEYS = {
  CONTENT_LIST_ITEM_REMOVED: 'content-list-item-removed',
  CONTENT_ITEM_LIKES_UPDATED: 'content-item-likes-updated',
  CONTENT_ITEM_COMMENTS_UPDATED: 'content-item-comments-updated',
  CONTENT_ITEM_UPDATED: 'content-item-updated',
  COMMENT_ADDED: 'comment-added',
  ROADMAP_ITEM_ADDED: 'roadmap-item-added',
  ROADMAP_ITEM_COMMENTS_UPDATED: 'roadmap-item-comments-updated',
} as const;

type AppEvents = {
  [APP_EVENT_KEYS.CONTENT_LIST_ITEM_REMOVED]: { mediaId: string };
  [APP_EVENT_KEYS.CONTENT_ITEM_LIKES_UPDATED]: { mediaId: string; likes: number };
  [APP_EVENT_KEYS.CONTENT_ITEM_COMMENTS_UPDATED]: { mediaId: string; comments: number };
  [APP_EVENT_KEYS.CONTENT_ITEM_UPDATED]: { mediaId: string; patch: Partial<UserMediaEntity> };
  [APP_EVENT_KEYS.COMMENT_ADDED]: { mediaId: string; commentId: string; parentId: string | null };
  // Empty-but-present "refresh" signal: a roadmap item was just added on the add page;
  // RoadmapPage listens and re-fetches the latest item itself (no payload needed).
  [APP_EVENT_KEYS.ROADMAP_ITEM_ADDED]: Record<string, never>;
  // A roadmap item's comment count changed (add/reply); RoadmapPage mirrors the
  // new total onto its in-memory item (mirrors CONTENT_ITEM_COMMENTS_UPDATED).
  [APP_EVENT_KEYS.ROADMAP_ITEM_COMMENTS_UPDATED]: { roadmapItemId: string; comments: number };
};

type StorageKeyEventData = { value: unknown };
type PrefixEventData = StorageKeyEventData & { key: string };

type EventHandler<T> = (data: T) => void;

class EventBus {
  private listeners = new Map<string, Set<EventHandler<unknown>>>();
  private prefixListeners = new Map<string, Set<EventHandler<unknown>>>();

  emit<K extends keyof AppEvents>(event: K, data: AppEvents[K]): void;
  emit(event: string, data: StorageKeyEventData): void;
  emit(event: string, data: unknown): void {
    this.listeners.get(event)?.forEach(fn => fn(data));

    // Notify prefix listeners whose prefix matches the start of this event key
    this.prefixListeners.forEach((handlers, prefix) => {
      if (event.startsWith(prefix)) {
        const prefixData = { ...(data as StorageKeyEventData), key: event };
        handlers.forEach(fn => fn(prefixData));
      }
    });
  }

  on<K extends keyof AppEvents>(event: K, handler: EventHandler<AppEvents[K]>): () => void;
  on(event: string, handler: EventHandler<StorageKeyEventData>): () => void;
  on(event: string, handler: EventHandler<any>): () => void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    const wrappedHandler = handler as EventHandler<unknown>;
    this.listeners.get(event)!.add(wrappedHandler);
    return () => {
      this.listeners.get(event)?.delete(wrappedHandler);
    };
  }

  onPrefix(prefix: string, handler: EventHandler<PrefixEventData>): () => void {
    if (!this.prefixListeners.has(prefix)) {
      this.prefixListeners.set(prefix, new Set());
    }
    const wrappedHandler = handler as EventHandler<unknown>;
    this.prefixListeners.get(prefix)!.add(wrappedHandler);
    return () => {
      this.prefixListeners.get(prefix)?.delete(wrappedHandler);
    };
  }
}

export const eventBus = new EventBus();
export { EventBus };
export type { AppEvents };
